// The macro body's placeholder vocabulary, and the function that substitutes it.
//
// WHY THIS LIVES ON THE CLIENT, ALONE. Nothing in `src/` ever expands a
// placeholder — every server-side mention of `body_template` is pass-through, and
// applying a macro is a client-side operation in v1 (docs/API.md §5.1). Contrast
// the ACTION vocabulary, which the server refuses writes on and therefore owns and
// serves (src/services/admin/macros.js, reaching the browser through the macros
// descriptor). The asymmetry is deliberate.
//
// The decisive argument is mechanical rather than philosophical: `resolve()` cannot
// cross the wire. A served list could carry only `key` and `description`, leaving
// the resolution here, and would make "a key is advertised but nothing substitutes
// it" representable again — the exact failure the keyword panel exists to remove.
// One module, holding the key AND its description AND its resolver, makes that
// combination unrepresentable, and test/web/macro-placeholders.test.js walks the
// list to prove every advertised key actually substitutes.
//
// This is a `.js` and not a `.jsx` on purpose: the test script has no JSX loader
// (`node --import ./test/support/hermetic-env.js --test`), so a placeholder list
// inside a component file cannot be tested at all — which is where this one was.

export const PLACEHOLDER = /\{\{\s*([a-z0-9_.]+)\s*\}\}/gi;

/**
 * Every placeholder the composer can substitute, in the order the keyword panel
 * lists them. Adding one is a single object literal; the panel picks it up with no
 * further edit.
 *
 * `description` is user-facing and describes what `resolve` ACTUALLY returns, not
 * what the key sounds like — `customer_first_name` is a whitespace split, not a
 * given-name parse, and saying so here is cheaper than the support ticket.
 */
export const MACRO_PLACEHOLDERS = [
  {
    key: 'customer_name',
    label: 'Customer name',
    description: "The contact's full name, as stored on the contact record.",
    resolve: (c) => c.customer?.name ?? '',
  },
  {
    key: 'customer_first_name',
    label: 'Customer first name',
    description: 'The first whitespace-delimited word of the contact’s name. "Mary Anne Kelly" gives "Mary". It is not a real given-name parse.',
    resolve: (c) => (c.customer?.name ?? '').trim().split(/\s+/)[0] ?? '',
  },
  {
    key: 'customer_email',
    label: 'Customer email',
    description: "The contact's email address.",
    resolve: (c) => c.customer?.email ?? '',
  },
  {
    // Added for the macro import: the desk's macros use the Zendesk equivalent
    // (`{{ticket.requester.phone}}`) ten times, in call-handling templates where the
    // number is the first thing an agent writes down. Without it those ten would
    // arrive as literal placeholder text in the composer.
    key: 'customer_phone',
    label: 'Customer phone',
    description: "The contact's phone number, as stored on the contact record.",
    resolve: (c) => c.customer?.phone ?? '',
  },
  {
    key: 'organization_name',
    label: 'Organization',
    description: 'The organization the caller belongs to.',
    resolve: (c) => c.organization?.name ?? '',
  },
  {
    key: 'location_name',
    label: 'Location',
    description: "The selected location's name.",
    resolve: (c) => c.location?.name ?? '',
  },
  {
    key: 'account_number',
    label: 'Account number',
    description: "The selected location's account number. Locations carry it, not organizations.",
    resolve: (c) => c.location?.account_number ?? '',
  },
  {
    key: 'agent_name',
    label: 'Agent name',
    description: 'The display name of the agent applying the macro.',
    resolve: (c) => c.user?.display_name ?? '',
  },
  {
    key: 'agent_first_name',
    label: 'Agent first name',
    description: 'The first whitespace-delimited word of that display name.',
    resolve: (c) => (c.user?.display_name ?? '').trim().split(/\s+/)[0] ?? '',
  },
  {
    key: 'ticket_subject',
    label: 'Subject',
    description: 'Whatever is in the Subject box at the moment the macro is applied.',
    resolve: (c) => c.subject ?? '',
  },
];

/**
 * Substitute the placeholders we can resolve from the form's current state.
 *
 * Anything unknown is left verbatim — a macro that reads "{{ticket.due_date}}" in
 * the composer is obviously unfinished, while one that silently became "" is a
 * sentence with a hole in it that gets emailed. A KNOWN key with nothing behind it
 * is left verbatim for the same reason: applying a macro before a caller is picked
 * leaves "{{customer_name}}" sitting in the composer rather than a gap.
 */
export function fillMacroTemplate(template, context) {
  if (!template) return '';
  const values = {};
  for (const placeholder of MACRO_PLACEHOLDERS) {
    values[placeholder.key] = placeholder.resolve(context ?? {});
  }

  return template.replace(PLACEHOLDER, (match, key) => {
    const replacement = values[key.toLowerCase()];
    return replacement === undefined || replacement === '' ? match : replacement;
  });
}
